// Add to home screen (R-59, features 10/16): nothing brings people back without email, so a phone icon does. The web app
// manifest (public/manifest.webmanifest) makes Magellan installable; this decides when the one-time hint shows: after a
// first calendar add or save, never in the first run, never once installed, and never again once dismissed.
// Android Chrome hands us its install prompt (beforeinstallprompt), kept here from the first page load; iPhone Safari has
// no prompt, so the hint says Share, then Add to Home Screen. Other browsers (desktop, in-app webviews) get no hint.
import { applePlatform, inAppBrowser } from './growth'
import { qaPreset } from './flags'

const DISMISSED = 'mg_install_done'
type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: InstallPrompt | null = null
const subs = new Set<() => void>()

/** Call once at start (App.tsx): keep Android's install prompt for when the hint is shown. */
export function watchInstallPrompt() {
  try {
    window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as InstallPrompt; subs.forEach((f) => f()) })
    window.addEventListener('appinstalled', () => { deferred = null; dismissInstall() })
  } catch { /* no window */ }
}
export const onInstallPrompt = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }

export function standalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
  } catch { return false }
}
const iosPhone = () => applePlatform() && (/iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints ?? 0) > 1)

/** Which hint this device can get: 'ios' (Share, Add to Home Screen), 'android' (our button opens Chrome's prompt), or null. */
export function installKind(): 'ios' | 'android' | null {
  if (qaPreset() === 'first_add') return deferred ? 'android' : 'ios'
  if (standalone() || inAppBrowser()) return null
  if (deferred) return 'android'
  if (iosPhone()) return 'ios'
  return null
}

export function installDismissed(): boolean {
  if (qaPreset() === 'first_add') return false
  try { return localStorage.getItem(DISMISSED) === '1' } catch { return false }
}
export function dismissInstall() { try { localStorage.setItem(DISMISSED, '1') } catch { /* asked again next time */ } }

/** Android: open Chrome's own install prompt. The hint is done either way. */
export async function promptInstall(): Promise<void> {
  const d = deferred
  deferred = null
  dismissInstall()
  if (!d) return
  try { await d.prompt(); await d.userChoice } catch { /* closed */ }
}
